<script setup lang="ts">
import { computed, inject, onMounted, ref, Ref, watch } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { cloneDeep, pick } from 'lodash';
import { getTextVariant } from '@/utils/stringUtils';
import { useMixinsRoom } from './useMixins';
import { useBookComposable } from './useBookComposable';
import { useModalComposable } from '@/views/composable';
import nationalList from '@/constants/national';
import { formatDate } from '@/utils/dateUtils';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputMask from 'primevue/inputmask';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import CopyPassenger from './CopyPassenger.vue';
import RoomRequestSelections from '@/components/book/RoomRequestSelections.vue';

const dialogRef = inject('dialogRef') as Ref;
const gType = dialogRef?.value?.options?.data?.gType;
const isUpdateChildForm = dialogRef?.value?.options?.data?.isUpdateChildForm;
const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const currentLanguage = getCurrentLocale();
const { checkInputAlphanumeric, checkInputAlpha, getAge, checkInputEmail, hasEditOccupacyPermission, areAllRoomInfosNull } = useBookComposable();
const { handleOpenErrorMessage } = useModalComposable();
const { room, passengerInfo, expandedRows, occupancy, today, titles, seatPrefOption, retDate, listPassenger, allowCopy, listChildren, priority, checkPriorityBooking } = useMixinsRoom(dialogRef);
const occupancyExpandedRows = ref([]);
const roomExpandedRows = ref({});
const childrenExpandedRows = ref({ children: {}, infants: {} });
const passportExpandedRows = ref([]);
const nationals = ref([...nationalList]);
const countIsLeadTrueHotel = ref<number>(0);
const maxLeadWedding = 2;
const maxLeadALl = 1;
const tableChildRenders: string[] = ['children', 'infants'];

const handleAddChild = (data, passenger, index?: number) => {
  if (!occupancy.value[data.index].passengers[index].children) {
    occupancy.value[data.index].passengers[index].children = [];
  }
  const numberAdultInRoom = data.data.passengers[index].people.length;
  const numberChildInRoom = data.data.passengers[index].children.length;
  const minMaxPerson = pick(data.data, ['maxPaxAdult', 'maxPaxChild', 'maxPaxRoom', 'minAdPerChild', 'minPaxAdult', 'minPaxChild', 'minPaxRoom']);
  if (numberAdultInRoom + numberChildInRoom >= minMaxPerson.maxPaxRoom) {
    handleOpenErrorMessage(t('booking.max_people'));
  } else if (numberChildInRoom >= minMaxPerson.maxPaxChild) {
    handleOpenErrorMessage(t('booking.max_child_per_room'));
  } else if (numberChildInRoom >= numberAdultInRoom * Number(minMaxPerson.minAdPerChild)) {
    handleOpenErrorMessage(t('booking.max_child_per_adult'));
  } else {
    occupancy.value[data.index].passengers[index].children.push({ ...cloneDeep(passengerInfo), key: `children${data.index}${index}${occupancy.value[data.index].passengers[index].children.length}` });
  }
};

const removeChild = (data, passengerIndex, childrenIndex) => {
  occupancy.value[data.index].passengers[passengerIndex].children.splice(childrenIndex, 1);
};

const copyPassenger = (hotelData, rowIndex, passengerIndex) => {
  const dialogRef = dialog.open(CopyPassenger, {
    data: {
      listPassenger
    },
    props: {
      header: t('my_quote.copy_passenger'),
      modal: true
    },
    emits: {
      onSelectPassenger: (passengerData) => {
        occupancy.value[hotelData.index].passengers[rowIndex].people[passengerIndex] = { ...passengerData.passenger };
        occupancy.value[hotelData.index].passengers[rowIndex].email = passengerData.email;
        dialogRef.close();
      }
    }
  });
};

const copyChildrenPassenger = (data, roomIndex, childrenIndex) => {
  const dialogRef = dialog.open(CopyPassenger, {
    data: {
      listPassenger: listChildren
    },
    props: {
      header: t('my_quote.copy_passenger'),
      modal: true
    },
    emits: {
      onSelectPassenger: (passengerData) => {
        occupancy.value[data.index].passengers[roomIndex].children[childrenIndex] = { ...passengerData.passenger };
        dialogRef.close();
      }
    }
  });
};

const headerLeadHotel = computed(() => {
  return gType.toUpperCase() === 'WEDDING' ? t('book_form.bride_groom') : t('book_form.group_leader');
});

const checkDisabledCheckbox = (isLead: boolean) => {
  return !isLead && (gType.toUpperCase() === 'WEDDING' ? countIsLeadTrueHotel.value >= maxLeadWedding : countIsLeadTrueHotel.value >= maxLeadALl);
};

const handleChildrenTotalHotel = (childrenTotalHotel: any, index: number, indexRoom: number) => {
  for (let i = 0; i < childrenTotalHotel; i++) {
    if (occupancy.value[index]?.passengers[indexRoom]?.children.length < childrenTotalHotel) {
      occupancy.value[index].passengers[indexRoom].children = [
        ...occupancy.value[index].passengers[indexRoom].children,
        { ...cloneDeep(passengerInfo), key: `children${index}${indexRoom}${occupancy.value[index].passengers[indexRoom].children.length}` }
      ];
    }
    if (occupancy.value[index]?.passengers[indexRoom].children.length > childrenTotalHotel) {
      occupancy.value[index].passengers[indexRoom].children = occupancy.value[index].passengers[indexRoom].children.slice(0, childrenTotalHotel);
    }
  }
};

const handleInfantsTotalHotel = (infantsTotalHotel: any, index: number, indexRoom: number) => {
  for (let i = 0; i < infantsTotalHotel; i++) {
    if (occupancy.value[index] && occupancy.value[index].passengers[indexRoom].infants.length < infantsTotalHotel) {
      occupancy.value[index].passengers[indexRoom].infants = [...occupancy.value[index].passengers[indexRoom].infants, { ...cloneDeep(passengerInfo), key: `infants${index}${indexRoom}${occupancy.value[index].passengers[indexRoom].infants.length}` }];
    }
    if (occupancy.value[index] && occupancy.value[index].passengers[indexRoom].infants.length > infantsTotalHotel) {
      occupancy.value[index].passengers[indexRoom].infants = occupancy.value[index].passengers[indexRoom].infants.slice(0, infantsTotalHotel);
    }
  }
};

const handleChildInitial = (room: any) => {
  const hotelRoomTemp = room;
  if (hotelRoomTemp && hotelRoomTemp.detailedOccupancy) {
    const filterArrays = Object.values(hotelRoomTemp.detailedOccupancy).filter((item: any) => Array.isArray(item));
    filterArrays.forEach((roomInfos: any, index: number) => {
      if (roomInfos && typeof roomInfos === 'object') {
        roomInfos.forEach((roomInfo: any, indexRoom: number) => {
          const childrenTotalHotel = roomInfo.children;
          const infantsTotalHotel = roomInfo.infants;
          if (childrenTotalHotel) {
            handleChildrenTotalHotel(childrenTotalHotel, index, indexRoom);
          } else {
            occupancy.value[index].passengers[indexRoom].children = [];
          }
          if (infantsTotalHotel) {
            handleInfantsTotalHotel(infantsTotalHotel, index, indexRoom);
          } else {
            occupancy.value[index].passengers[indexRoom].infants = [];
          }
        });
      }
    });
  }
};

const expandAllHotel = async () => {
  const occupancyHotelTemp = await occupancy.value;
  occupancyHotelTemp.forEach((item: any) => {
    occupancyExpandedRows.value = { ...occupancyExpandedRows.value, [item.field]: true };
    item.passengers.forEach((passenger: any) => {
      roomExpandedRows.value = { ...roomExpandedRows.value, [passenger.key]: true };
    });
  });
};

onMounted(() => {
  if (isUpdateChildForm || (!isUpdateChildForm && room && areAllRoomInfosNull(room))) {
    handleChildInitial(room);
  }
  expandAllHotel();
});

watch(
  () => occupancy.value,
  (newVal) => {
    countIsLeadTrueHotel.value = 0;
    newVal.forEach((room: any) => {
      room.passengers.forEach((passenger: any) => {
        passenger.people.forEach((person: any) => {
          if (person.isLead) {
            countIsLeadTrueHotel.value++;
          }
        });
      });
    });
  },
  { deep: true }
);
</script>
<template>
  <div class="booking-option-wrap">
    <div :class="[getTextVariant(currentLanguage, 'note-class', 'note-fr-class'), 'font-size-note']">{{ `*${t('book_form.note_room_requests')}` }}</div>
    <h2>{{ $t('booking.hotel_title') }}</h2>
    <div>
      {{ $t('booking.sub_title_one') }}<br />
      {{ $t('booking.sub_title_two') }} <br /><br />
    </div>
    <DataTable :value="occupancy" v-model:expandedRows="occupancyExpandedRows" dataKey="field" responsiveLayout="stack">
      <TableColumn :expander="true" class="w-3rem type-room" />
      <TableColumn field="occ" class="type-room">
        <template #body="slotProps">
          <b>{{ slotProps.data.label && $t(slotProps.data.label) }}</b>
          {{ $t('book_form.occupancy_for') }}
          <b>{{ room.hotel }} - {{ room.room }}</b>
        </template>
      </TableColumn>
      <template #expansion="slotProps">
        <div v-if="slotProps.data.passengers.length" class="orders-subtable">
          <DataTable tableClass="room-info-table" :value="slotProps.data.passengers" responsiveLayout="stack" data-key="key" v-model:expandedRows="roomExpandedRows">
            <TableColumn :expander="true" class="expand-column" />
            <TableColumn field="slotProps.data.room" :header="$t('book_form.room')" class="room-column">
              <template #body="slotProps">
                <div class="flex justify-content-center my-2">
                  {{ slotProps.index + 1 }}
                </div>
              </template>
            </TableColumn>
            <TableColumn :header="headerLeadHotel" class="room-column">
              <template #body="{ data }">
                <div v-for="(person, index) in data?.people" :key="index" :id="'lead-class-' + index" class="text-center">
                  <div class="my-2 w-8rem">
                    <Checkbox v-model="person.isLead" class="" :binary="true" :disabled="checkDisabledCheckbox(person?.isLead)" />
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="lastName" :header="$t('book_form.last_name')" class="w-14rem">
              <template #body="{ data }">
                <div class="w-20rem">
                  <div v-for="(person, index) in data.people" :key="index" class="my-2">
                    <InputText
                      v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                      v-model="person.lastName"
                      :maxlength="35"
                      :key="index"
                      type="text"
                      class="p-inputtext-sm"
                      @input="checkInputAlphanumeric(person, 'lastName')"
                      :tabindex="index"
                    />
                    <p v-else class="p-2">{{ person.lastName || '-' }}</p>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="firstName" :header="$t('book_form.first_name')" class="w-14rem">
              <template #body="{ data }">
                <div class="w-20rem">
                  <div v-for="(person, index) in data.people" :key="index" class="my-2">
                    <InputText
                      v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                      v-model="person.firstName"
                      :maxlength="35"
                      :key="index"
                      type="text"
                      class="p-inputtext-sm"
                      @input="checkInputAlphanumeric(person, 'firstName')"
                      :tabindex="index"
                    />
                    <p v-else class="p-2">{{ person.firstName || '-' }}</p>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="middleName" :header="$t('book_form.middle_name')" class="w-14rem">
              <template #body="{ data }">
                <div class="w-20rem">
                  <div v-for="(person, index) in data.people" :key="index" class="my-2">
                    <InputText
                      v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                      v-model="person.middleName"
                      :maxlength="35"
                      :key="index"
                      type="text"
                      class="p-inputtext-sm"
                      @input="checkInputAlphanumeric(person, 'middleName')"
                      :tabindex="index"
                    />
                    <p v-else class="p-2">{{ person.middleName || '-' }}</p>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="title" :header="$t('book_form.title')" class="w-7rem">
              <template #body="{ data }">
                <div class="w-7rem">
                  <div v-for="(person, index) in data.people" :key="index" class="my-2">
                    <Select v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)" v-model="person.title" :key="index" :options="titles" optionLabel="title" option-value="title" placeholder="" class="p-inputtext-sm" :tabindex="index" />
                    <p v-else class="p-2">{{ person.title || '-' }}</p>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="dateOfBirth" :header="$t('book_form.date_of_birth')" class="w-10rem">
              <template #body="{ data }">
                <div class="w-20rem">
                  <div v-for="(person, index) in data.people" :key="index" class="my-2 date-of-birth-class">
                    <DatePicker
                      v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                      v-model="person.dateOfBirth"
                      :key="index"
                      :maxDate="today"
                      inputId="basic"
                      dateFormat="dd-M-yy"
                      autocomplete="off"
                      class="p-inputtext-sm"
                      :placeholder="$t('common.date_format')"
                      :inputProps="{
                        tabindex: index
                      }"
                    >
                      <template #footer>
                        <p class="text-center">
                          <small>{{ $t('common.press_space') }}</small>
                        </p>
                      </template>
                    </DatePicker>
                    <p v-else class="p-2">{{ person.dateOfBirth ? formatDate(person.dateOfBirth, 'dd-LLL-yyyy', true, currentLanguage) : '-' }}</p>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="age" :header="$t('book_form.age')" class="w-4rem">
              <template #body="{ data }">
                <div class="w-4rem">
                  <div v-for="(person, index) in data?.people" :key="index" class="my-2">
                    <InputText v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)" v-model="person.age" :value="getAge(person.dateOfBirth, retDate)" readonly class="p-inputtext-sm" :tabindex="index" />
                    <p v-else class="p-2">{{ getAge(person.dateOfBirth, retDate) }}</p>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="telephone" :header="$t('book_form.telephone')" class="w-14rem">
              <template #body="{ data }">
                <div class="w-20rem">
                  <div v-for="(person, index) in data.people" :key="index" class="my-2 telephone-input-mask-class">
                    <InputMask v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)" :key="index" v-model="person.telephone" mask="(999) 999-9999? x99999" class="p-inputtext-sm" :tabindex="index" />
                    <p v-else class="p-2">{{ person.telephone || '-' }}</p>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="aeroPlan" :header="$t('book_form.aeroplan')" class="w-8rem">
              <template #body="{ data }">
                <div class="w-8rem">
                  <div v-for="(person, index) in data.people" :key="index" class="my-2">
                    <InputText v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)" v-model="person.aeroPlan" :maxlength="9" type="text" class="p-inputtext-sm" @input="checkInputAlphanumeric(person, 'aeroPlan')" :tabindex="index" />
                    <p v-else class="p-2">{{ person.aeroPlan || '-' }}</p>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="seatingPreference" :header="$t('book_form.seating_preference')" class="w-12rem">
              <template #body="{ data }">
                <div class="w-20rem">
                  <div v-for="(person, index) in data.people" :key="index" class="my-2 w-12rem seating-preference-class">
                    <Select
                      id="adult-seating-preference-id"
                      v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                      :key="index"
                      v-model="person.seatingPreference"
                      :options="seatPrefOption"
                      optionLabel="type"
                      option-value="type"
                      class="p-inputtext-sm"
                      :tabindex="index"
                    />
                    <p v-else class="p-2">{{ person.seatingPreference || '-' }}</p>
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="email" :header="$t('book_form.lead_name_email')" class="w-7rem">
              <template #body="{ data }">
                <div class="w-20rem">
                  <InputText :maxlength="50" type="text" v-model="data.email" class="p-inputtext-sm" v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)" @input="checkInputEmail(data, 'email')" />
                  <p v-else class="p-2">{{ data.email || '-' }}</p>
                </div>
              </template>
            </TableColumn>
            <TableColumn v-if="hasEditOccupacyPermission && allowCopy && !checkPriorityBooking(priority)">
              <template #body="{ data, index: rowIndex }">
                <div>
                  <div v-for="(person, index) in data.people" :key="index" class="my-2 copy-from-class">
                    <Button class="bg-primary white-space-nowrap copy-passenger-btn" :label="$t('my_quote.copy_from')" @click="copyPassenger(slotProps, rowIndex, index)" />
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="roomRequests" class="w-7rem">
              <template #header> {{ $t('book_form.room_requests') }}</template>
              <template #body="{ data }">
                <div class="w-34rem">
                  <RoomRequestSelections v-model:roomRequests="data.roomRequests"></RoomRequestSelections>
                </div>
              </template>
            </TableColumn>
            <TableColumn v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)">
              <template #body="{ data, index }">
                <div class="add-child-class">
                  <Button class="bg-primary white-space-nowrap" :label="$t('my_quote.add_child')" @click="handleAddChild(slotProps, data, index)" />
                </div>
              </template>
            </TableColumn>
            <template #expansion="{ data, index: passengerIndex }">
              <div>
                <DataTable tableClass="passpost-table adult-passport" :value="[data]" v-model:expandedRows="expandedRows[data.index]">
                  <TableColumn :expander="true" class="expand-column" />
                  <TableColumn>
                    <template #body> {{ $t('book_form.passport_information') }} </template>
                  </TableColumn>
                  <template #expansion="{ data }">
                    <DataTable :value="[data]">
                      <TableColumn class="font-bold pl-90">
                        <template #body>
                          <div v-for="(person, index) in data?.people" :key="index" class="p-3 white-space-nowrap my-2">
                            {{ `${$t('common.adult')} ( ${index + 1} ) ${$t('book_form.passport')}` }}
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn :header="$t('book_form.full_name')">
                        <template #body>
                          <div>
                            <div v-for="(person, index) in data?.people" :key="index" class="pr-8 my-2">
                              <InputText
                                class="w-full"
                                v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                type="text"
                                :placeholder="$t('book_form.full_name')"
                                :value="`${person.firstName ? person.firstName + ' ' : ''}${person.middleName ? person.middleName + ' ' : ''}${person.lastName ? person.lastName + ' ' : ''}`"
                                :readonly="true"
                                :tabindex="index"
                              />
                              <p v-else class="p-2">
                                {{
                                  person.firstName || person.middleName || person.lastName
                                    ? `${person.firstName || ''}
                                                                ${person.middleName || ''} ${person.lastName || ''}`
                                    : '-'
                                }}
                              </p>
                            </div>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="passportNumber" :header="$t('book_form.number')">
                        <template #body>
                          <div>
                            <div v-for="(person, index) in data?.people" :key="index" class="my-2">
                              <InputText
                                v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                type="text"
                                v-model="person.passport.passportNumber"
                                :maxlength="15"
                                :placeholder="$t('book_form.number')"
                                @input="checkInputAlphanumeric(person.passport, 'passportNumber')"
                                :tabindex="index"
                              />
                              <p v-else class="p-2">{{ person.passport.passportNumber || '-' }}</p>
                            </div>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="nationality" :header="$t('book_form.nationality_from')">
                        <template #body>
                          <div>
                            <div v-for="(person, index) in data?.people" :key="index" class="my-2">
                              <InputText
                                v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                type="text"
                                v-model="person.passport.nationality"
                                :maxlength="40"
                                :placeholder="$t('book_form.nationality_from')"
                                @input="checkInputAlpha(person.passport, 'nationality')"
                                :tabindex="index"
                              />
                              <p v-else class="p-2">{{ person.passport.nationality || '-' }}</p>
                            </div>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="expiryDate" :header="$t('book_form.expiry_date')">
                        <template #body>
                          <div>
                            <div v-for="(person, index) in data?.people" :key="index" class="my-2 expiry-date-class">
                              <DatePicker
                                v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                inputId="basic"
                                :placeholder="$t('book_form.expiry_date')"
                                v-model="person.passport.expiryDate"
                                autocomplete="off"
                                dateFormat="dd-M-yy"
                                class="p-inputtext-md"
                                :inputProps="{
                                  tabindex: index
                                }"
                              >
                                <template #footer>
                                  <p class="text-center">
                                    <small>{{ $t('common.press_space') }}</small>
                                  </p>
                                </template>
                              </DatePicker>
                              <p v-else class="p-2">{{ person.passport.expiryDate ? formatDate(person.passport.expiryDate, 'dd-LLL-yyyy', true, currentLanguage) : '-' }}</p>
                            </div>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="countryOfIssue" :header="$t('book_form.country_of_issue')">
                        <template #body>
                          <div>
                            <div v-for="(person, index) in data?.people" :key="index" class="my-2 country-of-issue-class">
                              <Select
                                id="adult-country-of-issue-id"
                                v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                v-model="person.passport.countryOfIssue"
                                filter
                                :options="nationals"
                                optionLabel="name"
                                optionValue="name"
                                :placeholder="$t('book_form.country_of_issue')"
                                class="p-inputtext-sm common-input-width"
                                :tabindex="index"
                              >
                                <template #option="slotProps">
                                  <p class="select-option">
                                    {{ slotProps.option.name }}
                                  </p>
                                </template>
                              </Select>
                              <p v-else class="p-2">{{ person.passport.countryOfIssue || '-' }}</p>
                            </div>
                          </div>
                        </template>
                      </TableColumn>
                    </DataTable>
                  </template>
                </DataTable>
                <div v-for="tableChildRender in tableChildRenders" :key="tableChildRender">
                  <DataTable tableClass="children-table" v-if="data[tableChildRender] && data[tableChildRender].length" :value="[data]" dataKey="key" responsiveLayout="stack" v-model:expandedRows="childrenExpandedRows[tableChildRender]">
                    <TableColumn :expander="true" class="expand-column" />
                    <TableColumn field="slotProps.data.room" :header="$t('book_form.room')" class="child-room">
                      <template #body="{ data }">
                        <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'children' + childrenIndex" class="p-3 white-space-nowrap">
                          <div class="flex justify-content-center white-space-nowrap">
                            {{ $t(tableChildRender === 'children' ? 'common.child' : 'common.infant') + ' ' + (childrenIndex + 1) }}
                          </div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn class="room-column"></TableColumn>
                    <TableColumn field="lastName" :header="$t('book_form.last_name')" class="w-23rem">
                      <template #body="{ data }">
                        <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'children' + childrenIndex" class="my-2">
                          <InputText
                            v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                            v-model="children.lastName"
                            :maxlength="35"
                            type="text"
                            class="p-inputtext-sm"
                            @input="checkInputAlphanumeric(children, 'lastName')"
                            :tabindex="childrenIndex"
                          />
                          <p v-else class="p-2">{{ children.lastName || '-' }}</p>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="firstName" :header="$t('book_form.first_name')" class="w-23rem">
                      <template #body="{ data }">
                        <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'children' + childrenIndex" class="my-2">
                          <InputText
                            v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                            v-model="children.firstName"
                            :maxlength="35"
                            type="text"
                            class="p-inputtext-sm"
                            @input="checkInputAlphanumeric(children, 'firstName')"
                            :tabindex="childrenIndex"
                          />
                          <p v-else class="p-2">{{ children.firstName || '-' }}</p>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="middleName" :header="$t('book_form.middle_name')" class="w-23rem">
                      <template #body="{ data }">
                        <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'children' + childrenIndex" class="my-2">
                          <InputText
                            v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                            v-model="children.middleName"
                            :maxlength="35"
                            type="text"
                            class="p-inputtext-sm"
                            @input="checkInputAlphanumeric(children, 'middleName')"
                            :tabindex="childrenIndex"
                          />
                          <p v-else class="p-2">{{ children.middleName || '-' }}</p>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="title" :header="$t('book_form.title')" class="w-7rem">
                      <template #body="{ data }">
                        <div class="w-7rem">
                          <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'children' + childrenIndex" class="my-2">
                            <Select v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)" v-model="children.title" :options="titles" optionLabel="title" option-value="title" class="p-inputtext-sm" :tabindex="childrenIndex" />
                            <p v-else class="p-2">{{ children.title || '-' }}</p>
                          </div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="dateOfBirth" :header="$t('book_form.date_of_birth')" class="w-10rem">
                      <template #body="{ data }">
                        <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenDOB' + childrenIndex" class="my-2 date-of-birth-class">
                          <div class="w-20rem">
                            <DatePicker
                              v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                              :maxDate="today"
                              inputId="basic"
                              v-model="children.dateOfBirth"
                              autocomplete="off"
                              dateFormat="dd-M-yy"
                              class="p-inputtext-sm"
                              :placeholder="$t('common.date_format')"
                              :inputProps="{
                                tabindex: childrenIndex
                              }"
                            >
                              <template #footer>
                                <p class="text-center">
                                  <small>{{ $t('common.press_space') }}</small>
                                </p>
                              </template>
                            </DatePicker>
                            <p v-else class="p-2">{{ children.dateOfBirth ? formatDate(children.dateOfBirth, 'dd-LLL-yyyy', true, currentLanguage) : '-' }}</p>
                          </div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="age" :header="$t('book_form.age')" class="w-4rem">
                      <template #body="{ data }">
                        <div class="w-4rem">
                          <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'children' + childrenIndex" class="my-2">
                            <InputText v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)" v-model="children.age" :value="getAge(children.dateOfBirth, retDate)" readonly class="p-inputtext-sm" :tabindex="childrenIndex" />
                            <p v-else class="p-2">{{ getAge(children.dateOfBirth, retDate) }}</p>
                          </div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="telephone" :header="$t('book_form.telephone')" class="w-23rem">
                      <template #body="{ data }">
                        <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenTelephone' + childrenIndex" class="my-2 telephone-input-mask-class">
                          <div>
                            <InputMask v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)" v-model="children.telephone" mask="(999) 999-9999? x99999" class="p-inputtext-sm" :tabindex="childrenIndex" />
                            <p v-else class="p-2">{{ children.telephone || '-' }}</p>
                          </div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="aeroPlan" :header="$t('book_form.aeroplan')" class="w-8rem">
                      <template #body="{ data }">
                        <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenAeroplan' + childrenIndex" class="my-2">
                          <div class="w-8rem">
                            <InputText
                              v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                              v-model="children.aeroPlan"
                              :maxlength="9"
                              type="text"
                              class="p-inputtext-sm"
                              @input="checkInputAlphanumeric(children, 'aeroPlan')"
                              :tabindex="childrenIndex"
                            />
                            <p v-else class="p-2">{{ children.aeroPlan || '-' }}</p>
                          </div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="seatingPreference" :header="$t('book_form.seating_preference')" class="w-12rem">
                      <template #body="{ data }">
                        <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenSeating' + childrenIndex" class="my-2 w-12rem seating-preference-class">
                          <div>
                            <Select
                              id="child-seating-preference-id"
                              v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                              v-model="children.seatingPreference"
                              :options="seatPrefOption"
                              optionLabel="type"
                              option-value="type"
                              class="p-inputtext-sm"
                              :tabindex="childrenIndex"
                            />
                            <p v-else class="p-2">{{ children.seatingPreference || '-' }}</p>
                          </div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn field="email" :header="$t('book_form.lead_name_email')" class="w-7rem">
                      <template #body="{ data }">
                        <div class="visibility-hidden">
                          <InputText :maxlength="50" type="text" v-model="data.email" class="p-inputtext-sm" v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)" @input="checkInputEmail(data, 'email')" />
                          <p v-else class="p-2">{{ data.email || '-' }}</p>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn v-if="hasEditOccupacyPermission && allowCopy && !checkPriorityBooking(priority) && tableChildRender === 'children'">
                      <template #body="{ data }">
                        <div>
                          <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenAction' + childrenIndex" class="my-2">
                            <Button class="bg-primary white-space-nowrap" :label="$t('my_quote.copy_from')" @click="copyChildrenPassenger(slotProps, passengerIndex, childrenIndex)" :tabindex="childrenIndex" />
                          </div>
                        </div>
                      </template>
                    </TableColumn>
                    <TableColumn v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)">
                      <template #body="{ data }">
                        <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenAction' + childrenIndex" class="my-2 gap-5 flex w-full justify-content-end">
                          <div class="relative">
                            <Button class="bg-primary white-space-nowrap visibility-hidden" :label="$t('my_quote.add_child')" :tabindex="childrenIndex" />
                            <Button
                              v-if="tableChildRender === 'children'"
                              class="bg-primary remove-btn white-space-nowrap absolute left-0"
                              :label="$t('common.buttons.remove')"
                              @click="removeChild(slotProps, passengerIndex, childrenIndex)"
                              :tabindex="childrenIndex"
                            />
                          </div>
                        </div>
                      </template>
                    </TableColumn>
                    <template #expansion="{ data }">
                      <div class="">
                        <DataTable tableClass="passpost-table child-passport" :value="[data]" v-model:expandedRows="passportExpandedRows[data.index]">
                          <TableColumn :expander="true" class="expand-column" />
                          <TableColumn>
                            <template #body> {{ $t('book_form.passport_information') }} </template>
                          </TableColumn>
                          <template #expansion="{ data }">
                            <DataTable :value="[data]">
                              <TableColumn class="font-bold pl-122">
                                <template #body="{ data }">
                                  <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenPassport' + childrenIndex" class="my-2">
                                    <div class="p-3 white-space-nowrap my-2">
                                      {{
                                        `${$t('common.child')} ( ${childrenIndex + 1} )
                                                                        ${$t('book_form.passport')}`
                                      }}
                                    </div>
                                  </div>
                                </template>
                              </TableColumn>
                              <TableColumn :header="$t('book_form.full_name')">
                                <template #body="{ data }">
                                  <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenPassportFullname' + childrenIndex" class="pr-8 my-2">
                                    <div>
                                      <InputText
                                        class="w-full"
                                        v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                        :placeholder="$t('book_form.full_name')"
                                        :value="`${children.firstName ? children.firstName + ' ' : ''}${children.middleName ? children.middleName + ' ' : ''}${children.lastName ? children.lastName + ' ' : ''}`"
                                        :readonly="true"
                                        :tabindex="childrenIndex"
                                      />
                                      <p v-else class="p-2">
                                        {{
                                          children.firstName || children.middleName || children.lastName
                                            ? `${children.firstName || ''}
                                                                            ${children.middleName || ''} ${children.lastName || ''}`
                                            : '-'
                                        }}
                                      </p>
                                    </div>
                                  </div>
                                </template>
                              </TableColumn>
                              <TableColumn field="passportNumber" :header="$t('book_form.number')">
                                <template #body="{ data }">
                                  <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenPassportNumber' + childrenIndex" class="my-2">
                                    <div>
                                      <InputText
                                        v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                        type="text"
                                        v-model="children.passport.passportNumber"
                                        :maxlength="15"
                                        :placeholder="$t('book_form.number')"
                                        @input="checkInputAlphanumeric(children.passport, 'passportNumber')"
                                        :tabindex="childrenIndex"
                                      />
                                      <p v-else class="p-2">{{ children.passport.passportNumber || '-' }}</p>
                                    </div>
                                  </div>
                                </template>
                              </TableColumn>
                              <TableColumn field="nationality" :header="$t('book_form.nationality_from')">
                                <template #body="{ data }">
                                  <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenPassportNationality' + childrenIndex" class="my-2">
                                    <div>
                                      <InputText
                                        v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                        type="text"
                                        v-model="children.passport.nationality"
                                        :maxlength="40"
                                        :placeholder="$t('book_form.nationality_from')"
                                        @input="checkInputAlpha(children.passport, 'nationality')"
                                        :tabindex="childrenIndex"
                                      />
                                      <p v-else class="p-2">{{ children.passport.nationality || '-' }}</p>
                                    </div>
                                  </div>
                                </template>
                              </TableColumn>
                              <TableColumn field="expiryDate" :header="$t('book_form.expiry_date')">
                                <template #body="{ data }">
                                  <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenPassportExpiry' + childrenIndex" class="my-2 expiry-date-class">
                                    <div>
                                      <DatePicker
                                        v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                        inputId="basic"
                                        v-model="children.passport.expiryDate"
                                        autocomplete="off"
                                        dateFormat="dd-M-yy"
                                        :placeholder="$t('book_form.expiry_date')"
                                        class="p-inputtext-md"
                                        :inputProps="{
                                          tabindex: childrenIndex
                                        }"
                                      >
                                        <template #footer>
                                          <p class="text-center">
                                            <small>{{ $t('common.press_space') }}</small>
                                          </p>
                                        </template>
                                      </DatePicker>
                                      <p v-else class="p-2">{{ children.passport.expiryDate ? formatDate(children.passport.expiryDate, 'dd-LLL-yyyy', true, currentLanguage) : '-' }}</p>
                                    </div>
                                  </div>
                                </template>
                              </TableColumn>
                              <TableColumn field="countryOfIssue" :header="$t('book_form.country_of_issue')">
                                <template #body="{ data }">
                                  <div v-for="(children, childrenIndex) in data?.[tableChildRender]" :key="'childrenPassportCountry' + childrenIndex" class="my-2 country-of-issue-class">
                                    <div>
                                      <Select
                                        id="child-country-of-issue-id"
                                        v-if="hasEditOccupacyPermission && !checkPriorityBooking(priority)"
                                        v-model="children.passport.countryOfIssue"
                                        filter
                                        :options="nationals"
                                        optionLabel="name"
                                        optionValue="name"
                                        :placeholder="$t('book_form.country_of_issue')"
                                        class="p-inputtext-sm common-input-width"
                                        :tabindex="childrenIndex"
                                      >
                                        <template #option="slotProps">
                                          <p class="select-option">
                                            {{ slotProps.option.name }}
                                          </p>
                                        </template>
                                      </Select>
                                      <p v-else class="p-2">{{ children.passport.countryOfIssue || '-' }}</p>
                                    </div>
                                  </div>
                                </template>
                              </TableColumn>
                            </DataTable>
                          </template>
                        </DataTable>
                      </div>
                    </template>
                  </DataTable>
                </div>
              </div>
            </template>
          </DataTable>
        </div>
      </template>
    </DataTable>
    <div v-if="!occupancy || (occupancy && !occupancy.length)" class="text-center mt-4">
      <h5>{{ $t('book_form.note') }}</h5>
    </div>
  </div>
</template>
